import {
  FormLabel,
  forwardRef,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightAddon,
  InputRightElement,
  Stack,
  Text,
} from "@chakra-ui/react";
import { ChangeEvent, KeyboardEvent, useEffect, useState } from "react";
import { RiCloseLine, RiSearchLine } from "react-icons/ri";

interface SearchInputProps {
  label?: string;
  placeholder?: string;
  onSearch?: (searchTerm: string) => void | Promise<void>;
  onSearchChange?: (searchTerm: string) => void | Promise<void>;
  onClear?: () => void;
  value?: string;
  variant?: "leftIcon" | "rightIcon";
  inputProps?: InputProps;
}

export const SearchInput = forwardRef((props: SearchInputProps, ref) => {
  const {
    label,
    placeholder = "Search by...",
    onSearch,
    onSearchChange,
    onClear,
    value,
    variant = "rightIcon",
    inputProps,
  } = props;
  const [searchTerm, setSearchTerm] = useState<string>(value ?? "");
  useEffect(() => {
    setSearchTerm(value ?? "");
  }, [value]);

  const handleSearchAction = () => {
    if (onSearch) onSearch(searchTerm);
  };

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    if (onSearchChange) onSearchChange(event.target.value);
    setSearchTerm(event.target.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;

    handleSearchAction();
  };

  const handleClearSearchTerm = () => {
    setSearchTerm("");
    if (onClear) onClear();
  };

  const input = (
    <InputGroup>
      {variant === "leftIcon" ? (
        <InputLeftElement pointerEvents="none">
          <Icon as={RiSearchLine} color="gray.400" />
        </InputLeftElement>
      ) : null}
      {variant === "rightIcon" ? (
        <InputRightElement
          children={
            searchTerm ? (
              <IconButton
                aria-label="clear-search-icon"
                icon={<RiCloseLine />}
                colorScheme="gray"
                background="none"
                size="sm"
              />
            ) : (
              ""
            )
          }
          onClick={handleClearSearchTerm}
          mr={12}
        />
      ) : null}
      <Input
        type="search"
        placeholder={placeholder}
        onChange={handleChangeInput}
        onKeyDown={handleKeyDown}
        value={searchTerm}
        sx={{ "::-webkit-search-cancel-button": { display: "none" } }}
        {...inputProps}
        ref={ref}
      />
      {variant === "rightIcon" ? (
        <InputRightAddon
          children={
            <IconButton
              aria-label="search-icon"
              icon={<RiSearchLine />}
              colorScheme="gray"
              background="none"
              borderTopLeftRadius={0}
              borderBottomLeftRadius={0}
            />
          }
          onClick={handleSearchAction}
          background="none"
          padding="0"
        />
      ) : null}
    </InputGroup>
  );

  return (
    <Stack width="100%">
      {label ? (
        // The input is nested inside the label, so FormLabel's default bottom
        // margin lands under the input as dead space instead of spacing the
        // label from it. The Text below already provides that spacing.
        <FormLabel marginBottom={0}>
          <Text mb="2">{label}</Text>
          {input}
        </FormLabel>
      ) : (
        input
      )}
    </Stack>
  );
});
